import { Add, CheckOutlined } from "@payping/icons";
import classnames from "classnames";
import { Dispatch, FC, Key, RefObject, SetStateAction } from "react";
import { Button, Divider, Empty, Row, Spin, Tag, prefixCls } from "@payping/web-components";
import { OptionType } from "./types";
import { getOptionLabel, handleDeselect, handleSelect } from "./utils";

interface Props {
  create: {
    label?: string;
    onClick?: () => void;
  };
  filteredOptions?: OptionType[];
  filteredCategoryOptions?: OptionType[];
  allSelected?: OptionType[];
  setAllSelected: Dispatch<SetStateAction<OptionType[] | undefined>>;
  category?: {
    filterOption?: true | ((inputValue: string, option?: OptionType) => boolean);
    label?: string;
    loading?: boolean;
    options?: OptionType[];
    onDeselect?: (option: OptionType) => void;
    onSelect?: (option: OptionType) => void;
    value?: string[];
  };
  activeOption?: number;
  setActiveOption: Dispatch<SetStateAction<number>>;
  setVisibleDropdown: Dispatch<SetStateAction<boolean | undefined>>;
  setSearchValue: Dispatch<SetStateAction<string>>;
  loading?: boolean;
  overlayRef: RefObject<HTMLDivElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  onSelect?: (value: OptionType) => void;
  onDeselect?: (value: OptionType) => void;
  onChange?: (value: OptionType[]) => void;
}

export const CreateDropdown: FC<Props> = (props: Props) => {
  const {
    create,
    category,
    filteredCategoryOptions,
    filteredOptions,
    allSelected,
    setAllSelected,
    setActiveOption,
    setVisibleDropdown,
    setSearchValue,
    activeOption,
    loading,
    overlayRef,
    inputRef,
    onChange,
    onDeselect,
    onSelect,
    ...otherProps
  } = props;

  const createSection = (
    <Button
      block
      variant="link"
      style={{ justifyContent: "flex-start", padding: "0 1vw", textAlign: "right" }}
      onClick={(event): void => {
        event.preventDefault();
        event.stopPropagation();
        setVisibleDropdown(false);
        create?.onClick?.();
      }}
    >
      <Add style={{ width: "1vw" }} /> {create.label}
    </Button>
  );

  const optionsSection = loading ? (
    <div style={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
      <Spin />
    </div>
  ) : filteredOptions && filteredOptions.length ? (
    filteredOptions.map((option, index) => {
      const isSelected = Boolean(allSelected?.find((item) => item.type !== "category" && item.value === option.value));
      return (
        <div
          className={classnames(
            `${prefixCls}-custom-select-option-item`,
            activeOption === index && `${prefixCls}-custom-select-option-item-active`,
            isSelected && `${prefixCls}-custom-select-option-item-selected`,
          )}
          onClick={(): void => {
            if (isSelected)
              setAllSelected(
                handleDeselect({
                  allSelected,
                  isCategory: option.type === "category",
                  onChange,
                  onDeselect: option.type === "category" ? category?.onDeselect : onDeselect,
                  option,
                }),
              );
            else {
              setAllSelected(
                handleSelect({
                  allSelected,
                  onChange,
                  onSelect: option.type === "category" ? category?.onSelect : onSelect,
                  option,
                }),
              );
              if (inputRef.current) inputRef.current.value = "";
              setSearchValue("");
            }
          }}
          onMouseOver={() => {
            setActiveOption(index);
          }}
          key={index}
        >
          {getOptionLabel(option)}
          {isSelected && <CheckOutlined className="color--cta" width={"14px"} />}
        </div>
      );
    })
  ) : (
    <div>
      <Empty description={"مشتری مورد نظر پیدا نشد"} />
    </div>
  );

  const categoriesSection = Boolean(filteredCategoryOptions && filteredCategoryOptions.length) && (
    <>
      <Divider className="border--gray300" style={{ margin: "4px 0" }} />
      <div style={{ padding: "0.5vw 1vw" }}>
        {category?.label}
        <div style={{ display: "flex", flexWrap: "wrap", marginTop: "0.5vw" }}>
          {category?.loading ? (
            <Row style={{ flexGrow: 1 }} align={"middle"} justify={"center"}>
              <Spin />
            </Row>
          ) : (
            filteredCategoryOptions?.map((option, index) => {
              const isSelected = Boolean(
                allSelected?.find((cat) => cat.type === "category" && cat.value === option.value),
              );
              return (
                <Tag
                  key={option?.key as Key}
                  closable={isSelected}
                  onClick={(): void => {
                    if (isSelected)
                      handleDeselect({
                        allSelected,
                        isCategory: option.type === "category",
                        onChange,
                        onDeselect: option.type === "category" ? category?.onDeselect : onDeselect,
                        option,
                      });
                    else {
                      handleSelect({
                        allSelected,
                        onChange,
                        onSelect: option.type === "category" ? category?.onSelect : onSelect,
                        option,
                      });
                      if (inputRef.current) inputRef.current.value = "";
                      setSearchValue("");
                    }
                  }}
                  onClose={() =>
                    handleDeselect({
                      allSelected,
                      isCategory: option.type === "category",
                      onChange,
                      onDeselect: option.type === "category" ? category?.onDeselect : onDeselect,
                      option,
                    })
                  }
                  color={
                    isSelected
                      ? activeOption === index + (filteredOptions?.length || 0)
                        ? "geekblue"
                        : "processing"
                      : activeOption === index + (filteredOptions?.length || 0)
                        ? "geekblue"
                        : undefined
                  }
                  // @ts-ignore: missing
                  onMouseOver={() => {
                    setActiveOption(index + (filteredOptions?.length || 0));
                  }}
                  style={{ marginBottom: "0.5vw" }}
                >
                  <a>{getOptionLabel(option)}</a>
                </Tag>
              );
            })
          )}
        </div>
      </div>
    </>
  );

  return (
    <div ref={overlayRef} className={`${prefixCls}-custom-select-option-list`} {...otherProps}>
      {createSection}
      <Divider className="border--gray300" style={{ margin: "4px 0" }} />
      {optionsSection}
      {categoriesSection}
    </div>
  );
};

export default CreateDropdown;
